import React, { createContext, useState, useContext, useCallback, useRef, useEffect } from 'react';
import { getChatMessages, sendChatMessage, markChatMessagesRead } from '../services/api';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const pollingRef = useRef(null);

  const fetchMessages = useCallback(async ({ silent = false } = {}) => {
    try {
      setLoading(true);
      const data = await getChatMessages();
      const msgs = data.messages || data || [];
      setMessages(msgs);
      const unread = msgs.filter((m) => !m.read && m.sender === 'support').length;
      setUnreadCount(unread);
    } catch (err) {
      if (!silent) {
        console.error('[Chat] Error fetching messages:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const send = useCallback(async (text) => {
    if (!text.trim()) return;
    try {
      setSending(true);
      const newMsg = await sendChatMessage(text.trim());
      setMessages((prev) => [...prev, newMsg]);
    } catch (err) {
      console.error('[Chat] Error sending message:', err);
    } finally {
      setSending(false);
    }
  }, []);

  const markRead = useCallback(async () => {
    try {
      await markChatMessagesRead();
      setUnreadCount(0);
      setMessages((prev) => prev.map((m) => ({ ...m, read: true })));
    } catch (err) {
      console.error('[Chat] Error marking messages read:', err);
    }
  }, []);

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        fetchMessages();
        markRead();
      }
      return next;
    });
  }, [fetchMessages, markRead]);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Polling every 30s when chat is open
  useEffect(() => {
    if (isOpen) {
      pollingRef.current = setInterval(fetchMessages, 30000);
    } else {
      clearInterval(pollingRef.current);
    }
    return () => clearInterval(pollingRef.current);
  }, [isOpen, fetchMessages]);

  // Initial unread count fetch — silent to avoid console noise on startup
  useEffect(() => {
    fetchMessages({ silent: true });
  }, [fetchMessages]);

  return (
    <ChatContext.Provider
      value={{
        messages,
        unreadCount,
        loading,
        sending,
        isOpen,
        toggleChat,
        closeChat,
        send,
        fetchMessages,
        markRead,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
